import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserInfo } from "@redux/slices/authSlice";
import { parseError } from "../utils/utils";
import ModalConfirm from "@components/misc/ModalConfirm";
import config from "../config";
import ScrollContainer from "@components/misc/ScrollContainer";
import { Link } from "react-router-dom";

const API_BASE_URL = config.API_BASE_URL;

// Helper function to normalize role names to lowercase
const normalizeRole = (role) => role?.toLowerCase() || "user";

// Available roles configuration - moved outside component to prevent recreation
const availableRoles = {
  superadmin: ["masteradmin", "admin", "user"],
  masteradmin: ["masteradmin", "admin", "user"],
  admin: ["admin", "user"],
  user: ["user"],
};

const MembersList = () => {
  const [members, setMembers] = useState([]);
  const [errors, setErrors] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
    role: "user",
    active: true,
  });

  const [editMemberId, setEditMemberId] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
    active: true,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(window.innerWidth > 1440 ? 8 : 10);
  const [totalMembers, setTotalMembers] = useState(0);
  const [listFilterStatus, setListFilterStatus] = useState("All");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userInfo = useSelector(selectUserInfo);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const handleResize = () => {
      const newPageSize = window.innerWidth > 1440 ? 8 : 10;
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when size changes
        setIsLoading(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pageSize]);

  useEffect(() => {
    // Only run if we have stable user info and haven't already loaded
    if (!userInfo || !userInfo.role || !isLoading) {
      return;
    }

    const checkAccessAndFetchData = async () => {
      // Check if user has a valid token
      if (!token) {
        toast.error("Please log in to access this page.");
        navigate("/login");
        return;
      }

      // Check if user info is available
      if (!userInfo || !userInfo.role) {
        toast.error("User information not available. Please log in again.");
        navigate("/login");
        return;
      }

      // If user role is "user", redirect to home page
      if (userInfo.role.toLowerCase() === "user") {
        toast.error("Access denied. You don't have permission to view this page.");
        navigate("/");
        return;
      }

      // If user has access, fetch members
      await fetchMembers();
      setIsLoading(false);
    };

    checkAccessAndFetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, token, userInfo?.id, userInfo?.role, isLoading]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/members/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          page_size: pageSize,
          status: listFilterStatus === "All" ? undefined : listFilterStatus.toLowerCase(),
        },
      });

      // Check if API supports pagination
      if (response.data.results && response.data.count !== undefined) {
        // Paginated response
        setMembers(response.data.results);
        setTotalMembers(response.data.count);
      } else {
        // Non-paginated response (direct array)
        setMembers(response.data || []);
        setTotalMembers(response.data?.length || 0);
      }
    } catch (error) {
      toast.error(parseError(error));
      console.error("Error fetching members:", error);
    } finally {
      setIsLoading(false); // Always set loading to false when done
    }
  };

  const rolesToShow = useMemo(() => {
    return availableRoles[userInfo?.role?.toLowerCase()] || [];
  }, [userInfo?.role]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/users/members/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Member deleted successfully!");
      fetchMembers();
    } catch (error) {
      toast.error(parseError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const confirmDelete = () => {
    if (memberToDelete) {
      handleDelete(memberToDelete.id);
      closeDeleteModal();
    }
  };

  const totalPages = () => {
    return Math.ceil(totalMembers / pageSize);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages()) {
      setCurrentPage(page);
      setIsLoading(true); // Show loading state when changing pages
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1); // Reset to first page
    setIsLoading(true); // Show loading state
    fetchMembers();
  };

  const SkeletonRow = () => {
    return (
      <>
        {[...Array(pageSize)].map((_, index) => (
          <tr key={index}>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-52 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-28 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-28 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-16 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-32 bg-gray-200 rounded-md"></div>
              </div>
            </td>
            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium">
              <div className="my-1 animate-pulse">
                <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  const getStatus = (status) => {
    if (status === "Active") {
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-bold text-green-700 border border-green-200 shadow-sm">
          {status}
        </span>
      );
    } else if (status === "Inactive") {
      return (
        <span className="inline-flex items-center gap-x-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700 border border-red-200 shadow-sm">
          {status}
        </span>
      );
    }
  };


  const handleAddMember = async (e) => {
    e.preventDefault();

    // Final validation check
    if (Object.values(errors).some(err => err)) {
      toast.error("Please fix all errors before submitting.");
      return;
    }

    const hasEmptyFields = !formData.email || !formData.first_name || !formData.last_name || !formData.password || !formData.confirm_password;
    if (hasEmptyFields) {
      toast.error("All required fields must be filled.");
      return;
    }

    if (/\s/.test(formData.password)) {
      toast.error("Spaces are not allowed in passwords.");
      return;
    }

    try {

      await axios.post(
        `${API_BASE_URL}/api/users/members/add/`,
        {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password,
          password2: formData.confirm_password,
          active: formData.active,
          role: formData.role,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Member added successfully!");
      setShowAddForm(false);
      setFormData({
        email: "",
        first_name: "",
        last_name: "",
        password: "",
        confirm_password: "",
        role: rolesToShow[0] || "user",
        active: true,
      });
      fetchMembers();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    let newError = "";

    if (name === "password" || name === "confirm_password") {
      // Spaces allowed in state for now, will be checked on submit
    }
    if (name === "first_name" || name === "last_name") {
      newValue = newValue.replace(/[^A-Za-z]/g, "");
    }

    // Real-time validation
    if (name === "email") {
      if (!newValue.trim()) {
        newError = "Work Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
        newError = "Invalid email address";
      } else {
        const personalDomains = ["@gmail.", "@yahoo.", "@outlook."];
        if (personalDomains.some(d => newValue.toLowerCase().includes(d))) {
          newError = "Personal emails (gmail, yahoo, outlook) are not allowed";
        }
      }
    }

    if (name === "first_name" || name === "last_name") {
      if (!newValue.trim() && name === "first_name") {
        newError = "First name is required";
      } else if (newValue.trim() && !/^[A-Za-z ]{2,}$/.test(newValue)) {
        newError = "Only letters allowed, min 2 characters";
      }
    }

    if (name === "password") {
      if (!newValue) {
        newError = "Password is required";
      } else if (newValue.length < 8) {
        newError = "Minimum 8 characters required";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newValue)) {
        newError = "Must include A-Z, a-z, 0-9, and special char";
      } else if (/(.)\1{2,}/.test(newValue)) {
        newError = "No more than 2 same characters continuously allowed";
      } else if (/([@$!%*?&])\1/.test(newValue)) {
        newError = "Consecutive identical special characters (like @@, !!) not allowed";
      }
    }

    if (name === "confirm_password") {
      if (newValue !== formData.password) {
        newError = "Passwords do not match";
      }
    }

    setErrors(prev => ({ ...prev, [name]: newError }));
    setFormData({ ...formData, [name]: newValue });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    let newError = "";

    if (name === "first_name" || name === "last_name") {
      newValue = newValue.replace(/[^A-Za-z]/g, "");
    }

    // Real-time validation for edit
    if (name === "email") {
      if (!newValue.trim()) {
        newError = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
        newError = "Invalid email address";
      } else {
        const personalDomains = ["@gmail.", "@yahoo.", "@outlook."];
        if (personalDomains.some(d => newValue.toLowerCase().includes(d))) {
          newError = "Personal emails (gmail, yahoo, outlook) are not allowed";
        }
      }
    }

    if (name === "first_name" || name === "last_name") {
      if (!newValue.trim() && name === "first_name") {
        newError = "First name is required";
      } else if (newValue.trim() && !/^[A-Za-z ]{2,}$/.test(newValue)) {
        newError = "Only letters allowed, min 2 characters";
      }
    }

    setErrors(prev => ({ ...prev, [name]: newError }));
    setEditFormData({ ...editFormData, [name]: newValue });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Final validation check for edit (exclude password if not present)
    if (errors.email || errors.first_name || errors.last_name) {
      toast.error("Please fix all errors before submitting.");
      return;
    }

    if (!editFormData.email || !editFormData.first_name || !editFormData.last_name) {
      toast.error("Required fields cannot be empty.");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/users/members/${editMemberId}/edit/`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Member updated successfully!");
      setShowEditForm(false);
      setEditMemberId(null);
      setEditFormData({
        email: "",
        first_name: "",
        last_name: "",
        role: "user",
        active: true
      });
      fetchMembers();
    } catch (error) {
      toast.error(parseError(error));
    }
  };

  // Update form data when user role changes
  useEffect(() => {
    if (userInfo?.role && rolesToShow.length > 0) {
      setFormData(prev => ({
        ...prev,
        role: rolesToShow[0] || "user",
        active: true
      }));
    }
  }, [userInfo?.role, rolesToShow]);

  // Refetch members when page, page size, or filter changes
  useEffect(() => {
    if (userInfo?.role && userInfo.role.toLowerCase() !== "user") {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, listFilterStatus]);

  // Remove the error check since we don't have an error state variable

  return (
    <>
      <div
        className="mt-2 px-6 py-3 bg-white overflow-hidden"
        id="listing"
      >
        <section className="mx-auto w-[94%]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-x-3">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider">
                Members
              </h2>
              <span className="bg-blue-600 text-white rounded-full px-3 py-1 text-xs font-black shadow-lg">
                {isLoading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  totalMembers || '0'
                )}
              </span>
            </div>
            <div>
              {userInfo?.role && userInfo.role.toLowerCase() !== "user" && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95"
                >
                  Add Member
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 md:flex md:items-center md:justify-between">
            <ScrollContainer>
              <div className="flex items-center gap-x-3 pr-4 whitespace-nowrap py-1">
                <button
                  onClick={() => {
                    setListFilterStatus("All");
                    setCurrentPage(1);
                    setIsLoading(true);
                  }}
                  className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-200 shadow-sm active:scale-95 whitespace-nowrap ${listFilterStatus === "All"
                    ? "bg-blue-600 text-white border border-blue-700"
                    : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                    }`}
                >
                  View all
                </button>

                <button
                  onClick={() => {
                    setListFilterStatus("Active");
                    setCurrentPage(1);
                    setIsLoading(true);
                  }}
                  className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-200 shadow-sm active:scale-95 whitespace-nowrap ${listFilterStatus === "Active"
                    ? "bg-blue-600 text-white border border-blue-700"
                    : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                    }`}
                >
                  Active
                </button>

                <button
                  onClick={() => {
                    setListFilterStatus("Inactive");
                    setCurrentPage(1);
                    setIsLoading(true);
                  }}
                  className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-200 shadow-sm active:scale-95 whitespace-nowrap ${listFilterStatus === "Inactive"
                    ? "bg-blue-600 text-white border border-blue-700"
                    : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                    }`}
                >
                  Inactive
                </button>



                <button
                  onClick={handleRefresh}
                  title="Refresh Cache"
                  className="flex items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-4 py-2 text-sm font-bold text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95 whitespace-nowrap"
                >
                  <svg className="h-4 w-4 text-[#3b82f6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                <div className="h-6 w-px bg-gray-200 mx-2"></div>
                <Link
                  to="/integrations/listing"
                  className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 text-sm font-bold rounded-full transition-all duration-300 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95 whitespace-nowrap"
                >
                  Integrations
                </Link>
                <Link
                  to="/agent"
                  className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 text-sm font-bold rounded-full transition-all duration-300 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95 whitespace-nowrap"
                >
                  Launch Agent
                </Link>
              </div>
            </ScrollContainer>
          </div>

          <div className="mt-2 flex flex-col">
            <ScrollContainer className="rounded-lg shadow-sm">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-blue-400 bg-white md:rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          First Name
                        </th>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Last Name
                        </th>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Role
                        </th>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Active
                        </th>
                        <th scope="col" className="px-2 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Created On
                        </th>
                        <th scope="col" className="px-4 py-4 text-left text-sm font-bold text-gray-900 tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 border-t border-gray-200">
                      {isLoading && <SkeletonRow />}
                      {!isLoading && members.map((member) => (
                        <tr key={member.id} className={`${member.id === userInfo?.id ? 'bg-blue-50/50' : ''}`}>
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900">{member.email}</span>
                              {member.id === userInfo?.id && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black bg-blue-600 text-white shadow-sm tracking-wider">
                                  You
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                            <span className="text-gray-900">{member.first_name}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                            <span className="text-gray-900">{member.last_name}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                            <span className="text-gray-900">
                              {member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : "Not Available"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-sm">
                            {getStatus(member.active ? "Active" : "Inactive")}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-black">
                            <div className="flex items-center">
                              {member.created_on ? new Date(member.created_on).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 'N/A'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-1 py-2 text-sm">
                            {(() => {
                              const canEdit = (() => {
                                if (member.id === userInfo?.id) return false;
                                if (!member.role) {
                                  return userInfo?.role?.toLowerCase() === "superadmin";
                                }
                                if (userInfo?.role?.toLowerCase() === "superadmin") return true;
                                if (userInfo?.role?.toLowerCase() === "masteradmin" && normalizeRole(member.role) !== "superadmin") return true;
                                if (userInfo?.role?.toLowerCase() === "admin" && !["superadmin", "masteradmin"].includes(normalizeRole(member.role))) return true;
                                return false;
                              })();

                              return canEdit ? (
                                <div className="group relative inline-block">
                                  <button
                                    onClick={() => {
                                      setEditMemberId(member.id);
                                      setEditFormData({
                                        email: member.email,
                                        first_name: member.first_name,
                                        last_name: member.last_name,
                                        role: member.role || "user",
                                        active: member.active !== undefined ? member.active : true,
                                      });
                                      setShowEditForm(true);
                                    }}
                                    aria-label="Edit member details"
                                    className="rounded-full p-1.5 text-blue-500 transition-all duration-200 hover:bg-[#f0f7ff] hover:text-[#1e3a8a] active:scale-90"
                                  >
                                    <svg
                                      className="h-5 w-5"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                  <span className="pointer-events-none absolute -top-10 right-0 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                    Edit member details
                                  </span>
                                </div>
                              ) : null;
                            })()}

                            {(() => {
                              const canDelete = (() => {
                                if (member.id === userInfo?.id) return false;
                                if (!member.role) {
                                  return userInfo?.role?.toLowerCase() === "superadmin";
                                }
                                if (userInfo?.role?.toLowerCase() === "superadmin") return true;
                                if (userInfo?.role?.toLowerCase() === "masteradmin" && normalizeRole(member.role) !== "superadmin") return true;
                                if (userInfo?.role?.toLowerCase() === "admin" && !["superadmin", "masteradmin"].includes(normalizeRole(member.role))) return true;
                                return false;
                              })();

                              return canDelete ? (
                                <div className="group relative inline-block">
                                  <button
                                    onClick={() => openDeleteModal(member)}
                                    aria-label="Delete member"
                                    className="rounded-full p-1.5 text-red-500 transition-all duration-200 hover:bg-red-50 hover:text-red-600 active:scale-90"
                                  >
                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
                                    </svg>
                                  </button>
                                  <span className="pointer-events-none absolute -top-10 right-0 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                                    Delete member
                                  </span>
                                </div>
                              ) : null;
                            })()}
                          </td>
                        </tr>
                      ))}
                      {!isLoading && members.length === 0 && (
                        <tr>
                          <td colSpan="7" className="whitespace-nowrap px-4 py-2 text-center text-sm font-medium" style={{ color: "red" }}>
                            No members found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </ScrollContainer>
            {members.length > 0 && (
              <div className="mt-10 flex items-center justify-center px-4">
                <div className="flex select-none items-center gap-x-4">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 transition-all duration-300 rounded-full font-bold text-sm shadow-sm active:scale-95 ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none"
                      : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                      }`}
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Previous
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages() }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => currentPage !== page ? handlePageChange(page) : undefined}
                        className={`h-10 w-10 shrink-0 flex items-center justify-center transition-all duration-300 rounded-full text-sm font-bold shadow-sm active:scale-90 ${currentPage === page
                          ? "bg-blue-600 text-white border border-blue-700 shadow-md scale-105"
                          : "bg-white text-gray-700 border border-gray-200 hover:bg-[#f0f7ff] hover:text-[#1e3a8a] hover:border-[#cce3ff]"
                          }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages()}
                    className={`flex h-10 min-w-[120px] items-center justify-center gap-1 px-4 shrink-0 transition-all duration-300 rounded-full font-bold text-sm shadow-sm active:scale-95 ${currentPage === totalPages()
                      ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none"
                      : "bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md"
                      }`}
                  >
                    Next
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <div className="fixed top-24 right-4 w-[calc(100vw-2rem)] sm:w-[400px] h-fit bg-white border border-blue-300 shadow-2xl transition-all duration-300 rounded-2xl z-[40]">
          <div className="p-5 max-h-[calc(100vh-160px)] overflow-y-auto">
            {userInfo?.role && userInfo.role.toLowerCase() !== "user" ? (
              <>
                <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent tracking-wider">
                    Add Member
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setFormData({
                        email: "",
                        first_name: "",
                        last_name: "",
                        password: "",
                        confirm_password: "",
                        role: rolesToShow[0] || "user",
                        active: true,
                      });
                    }}
                    className="bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-4 py-1.5 rounded-full font-bold hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all duration-200 active:scale-95 shadow-sm text-xs"
                  >
                    Cancel
                  </button>
                </div>
                <form onSubmit={handleAddMember} className="space-y-[6px]">
                  <div className="space-y-[2px]">
                    <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                      Work Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="email@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.email ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                    />
                    {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.email}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-[2px]">
                      <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        placeholder="John"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.first_name ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                      />
                      {errors.first_name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.first_name}</p>}
                    </div>
                    <div className="space-y-[2px]">
                      <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        placeholder="Doe"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.last_name ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                      />
                      {errors.last_name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.last_name}</p>}
                    </div>
                  </div>

                  <div className="space-y-[2px]">
                    <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="••••••••"
                        value={formData.password || ""}
                        onChange={handleChange}
                        autoComplete="new-password"
                        required
                        className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.password ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-md"
                      >
                        {showPassword ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.password}</p>}
                  </div>

                  <div className="space-y-[2px]">
                    <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirm_password"
                        placeholder="••••••••"
                        value={formData.confirm_password || ""}
                        onChange={handleChange}
                        autoComplete="new-password"
                        required
                        className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.confirm_password ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-md"
                      >
                        {showConfirmPassword ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.confirm_password}</p>}
                  </div>

                  <div className="space-y-[2px]">
                    <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">User Role</label>
                    <div className="relative">
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full h-9 px-3 py-1.5 bg-white border border-blue-400 rounded-lg text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%233b82f6' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: "right 1rem center",
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "1.5em 1.5em",
                        }}
                      >
                        {rolesToShow.map((role) => (
                          <option key={role} value={role}>
                            {role === "superadmin" ? "Super Admin" :
                              role === "masteradmin" ? "Master Admin" :
                                role === "admin" ? "Admin" :
                                  role === "user" ? "User" : role}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 py-1 mt-1">
                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={(e) => setFormData({
                          ...formData,
                          active: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 bg-white border-blue-400 rounded focus:ring-blue-500 focus:ring-2 transition-all cursor-pointer"
                      />
                      <span className="group-hover:text-blue-600 transition-colors tracking-tight text-xs">Account active</span>
                    </label>
                  </div>

                  {userInfo?.role && userInfo.role.toLowerCase() !== "superadmin" && (
                    <div className="text-[11px] font-bold text-blue-700 bg-blue-50 p-2 rounded-lg border border-blue-200 shadow-sm leading-tight">
                      <p>Note: You can only assign roles that are at or below your current role level.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-10 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-blue-200 active:scale-[0.98] mt-6 text-sm tracking-widest uppercase text-center"
                  >
                    Create Member
                  </button>
                </form>
              </>
            ) : (
              <div className="p-4">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-500 bg-clip-text text-transparent uppercase tracking-wide">
                    Access Denied
                  </h2>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 rounded-full font-bold hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    Close
                  </button>
                </div>
                <div className="text-center py-6 text-indigo-400">
                  <p>You do not have permission to add members.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Member Form */}
      {showEditForm && (
        <div className="fixed top-24 right-4 w-[calc(100vw-2rem)] sm:w-[400px] h-fit bg-white border border-blue-300 shadow-2xl transition-all duration-300 rounded-2xl z-[40]">
          <div className="p-5 max-h-[calc(100vh-140px)] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-2">
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent tracking-wide">
                Edit Member
              </h2>
              <button
                onClick={() => {
                  setShowEditForm(false);
                  setEditMemberId(null);
                  setEditFormData({
                    email: "",
                    first_name: "",
                    last_name: "",
                    role: "user",
                    active: true
                  });
                }}
                className="bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-4 py-1.5 rounded-full font-bold hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all duration-200 active:scale-95 shadow-sm text-xs"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-[6px] mt-1">
              <div className="space-y-[2px]">
                <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                  required
                  className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.email ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                />
                {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.email}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-[2px]">
                  <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First Name"
                    value={editFormData.first_name}
                    onChange={handleEditFormChange}
                    required
                    className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.first_name ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                  />
                  {errors.first_name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.first_name}</p>}
                </div>

                <div className="space-y-[2px]">
                  <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last Name"
                    value={editFormData.last_name}
                    onChange={handleEditFormChange}
                    required
                    className={`w-full h-9 px-3 py-1.5 bg-white border ${errors.last_name ? 'border-red-500' : 'border-blue-400'} rounded-lg text-gray-900 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm`}
                  />
                  {errors.last_name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 tracking-tight">{errors.last_name}</p>}
                </div>
              </div>

              <div className="space-y-[2px]">
                <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">User Role</label>
                <div className="relative">
                  <select
                    name="role"
                    value={editFormData.role}
                    onChange={handleEditFormChange}
                    className="w-full h-9 px-3 py-1.5 bg-white border border-blue-400 rounded-lg text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%233b82f6' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: "right 1rem center",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "1.5em 1.5em",
                    }}
                  >
                    {rolesToShow.map((role) => (
                      <option key={role} value={role}>
                        {role === "superadmin" ? "Super Admin" :
                          role === "masteradmin" ? "Master Admin" :
                            role === "admin" ? "Admin" :
                              role === "user" ? "User" : role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 py-1 mt-1">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="active"
                    checked={editFormData.active}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      active: e.target.checked
                    })}
                    className="w-4 h-4 text-blue-600 bg-white border-blue-400 rounded focus:ring-blue-500 focus:ring-2 transition-all cursor-pointer"
                  />
                  <span className="group-hover:text-blue-600 transition-colors tracking-tight text-xs">Account Active</span>
                </label>
              </div>

              <button
                type="submit"
                className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-10 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-blue-200 active:scale-[0.98] mt-6 text-sm tracking-widest uppercase text-center"
              >
                Update Member Details
              </button>
            </form>
          </div>
        </div>
      )}

      <ModalConfirm
        isOpen={showDeleteModal}
        onCancel={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Member"
      >
        <div className="text-center">
          <p className="text-slate-600 font-medium mb-1">
            Are you sure you want to delete this member?
          </p>
          <p className="text-slate-500 text-sm mb-3">
            Integrations created by this member will become inaccessible.
          </p>
          {memberToDelete && (
            <div className="bg-gray-100/60 backdrop-blur-md p-3 rounded-xl border border-gray-200 shadow-inner mb-3">
              <p className="font-bold text-slate-800">
                {memberToDelete.first_name} {memberToDelete.last_name}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {memberToDelete.email}
              </p>
            </div>
          )}
          <p className="text-[11px] font-bold text-red-500 uppercase tracking-wider">
            This action cannot be undone
          </p>
        </div>
      </ModalConfirm>
    </>
  );
};

export default MembersList; 